import { jsx } from 'react/jsx-runtime';
import { Root as Slot } from '../../vendor/@radix-ui/react-slot/dist/index.mjs';
import { cva } from '../../vendor/class-variance-authority/dist/index.mjs';
import * as React from 'react';
import { useDevice } from '../../contexts/DeviceContext.js';
import { cn } from '../../lib/utils.js';
import { LineIcon } from './icons/LineIcon.js';

const buttonVariants = cva(
  "btn rounded-[50px] inline-flex pt-[2px] items-center justify-center gap-1 whitespace-nowrap text-sm font-weight-medium transition-colors disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_.ico-wrap]:-mt-[2px]",
  {
    variants: {
      variant: {
        default: `bg-primary-background-04 text-interactive-inverse 
                        [@media(hover:hover){&:hover:not(:active)}]:bg-interactive-primary-hovered [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered 
                        active:bg-interactive-primary-pressed active:text-interactive-inverse
                        disabled:bg-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        destructive: `btn-destructive text-interactive-inverse bg-base-black 
                        [@media(hover:hover){&:hover:not(:active)}]:bg-interactive-tertiary-hovered 
                        active:bg-interactive-tertiary-pressed 
                        disabled:bg-state-disabled-02 disabled:text-interactive-active-disabled-01`,
        scOutline: `border bg-base-white border-primary-02 text-primary-04 
                        [@media(hover:hover){&:hover:not(:active)}]:border-interactive-primary-hovered active:border-interactive-primary-hovered 
                        active:border-interactive-primary-pressed active:text-interactive-primary-pressed  
                        disabled:border-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        outline: `btn-outline border bg-base-white border-primary-02 text-primary-04 
                        [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered [@media(hover:hover){&:hover:not(:active)}]:border-interactive-primary-hovered 
                        active:border-interactive-primary-pressed active:text-interactive-primary-pressed  
                        disabled:border-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        ghost: "text-primary-04 rounded-[0px] body-lg font-weight-medium",
        link: `text-primary-04 rounded-[0px] 
                        [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered 
                        active:text-interactive-primary-pressed 
                        disabled:text-interactive-primary-disabled`,
        grayLink: `text-basic-04 rounded-[0px] 
                        [@media(hover:hover){&:hover:not(:active)}]:text-interactive-tertiary-hovered 
                        active:text-interactive-tertiary-pressed 
                        disabled:text-interactive-tertiary-disabled`,
        extra: `btn-extra text-primary-05 bg-background-04 border border-primary-03 rounded-tl-md rounded-tr-sm rounded-br-lg rounded-bl-sm 
                        [@media(hover:hover){&:hover:not(:active)}]:bg-interactive-extra-hovered [@media(hover:hover){&:hover:not(:active)}]:border-interactive-primary-hovered [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered 
                        active:bg-interactive-extra-hovered active:border-interactive-primary-pressed active:text-interactive-primary-pressed 
                        disabled:border-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        secondary: `btn-secondary text-primary-05 bg-primary-background-08 border border-primary-04 
                            [@media(hover:hover){&:hover:not(:active)}]:bg-interactive-extra-hovered [@media(hover:hover){&:hover:not(:active)}]:border-interactive-extra-hovered [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered 
                            active:bg-interactive-extra-pressed active:border-interactive-extra-pressed active:text-interactive-primary-pressed 
                            disabled:bg-base-white disabled:border-interactive-extra-disabled disabled:text-interactive-primary-disabled`,
        // selection: `btn-selection text-interactive-inverse bg-interactive-active-selected-02
        //         disabled:text-state-disabled-04 disabled:bg-state-disabled-02`,
        grayOutline: `btn-gray-outline border bg-background bg-base-white border-basic-03 text-basic-04 
                            [@media(hover:hover){&:hover:not(:active)}]:border-interactive-primary-hovered 
                            active:border-interactive-primary-pressed active:text-interactive-primary-pressed 
                            disabled:border-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        line: "bg-transfer text-primary-01 rounded-[0px] py-[18px] font-weight-bold border-t border-basic-04",
        brand: `bg-primary-background-04 text-interactive-inverse 
                        [@media(hover:hover){&:hover:not(:active)}]:bg-interactive-primary-hovered [@media(hover:hover){&:hover:not(:active)}]:text-interactive-primary-hovered 
                        disabled:bg-interactive-primary-disabled disabled:text-interactive-primary-disabled`,
        textLink: "body-lg text-basic-04 btn-link"
      },
      size: {
        default: "h-9 px-4 py-2",
        xs: "h-8 px-4 body-xs min-w-[60px]",
        sm: "h-8 px-4 body-sm min-w-[56px]",
        md: "h-10 px-5 body-md min-w-[66px]",
        lg: "h-12 px-3 body-lg min-w-[78px]",
        extra: "h-8 body-md px-4",
        icon: "h-9 w-9",
        link: "h-6 body-lg font-weight-medium",
        smLink: "h-5 body-sm font-weight-medium align-middle",
        xl: "h-[56px] px-4",
        xsm: "h-6 font-weight-400"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    },
    compoundVariants: [
      { variant: "default", size: "lg", className: "bg-primary-background-05" },
      { variant: "outline", size: "lg", className: "border-primary-03 text-primary-05" },
      { variant: "brand", size: "lg", className: "h-10 body-md font-weight-medium" }
    ]
  }
);
const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { isDesktop } = useDevice();
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(
          buttonVariants({ variant, size, className }),
          variant === "outline" && isDesktop && "leading-[40px]",
          size === "sm" && isDesktop && "h-8 px-5",
          size === "md" && isDesktop && "h-12 px-6",
          size === "lg" && isDesktop && "h-14 px-7",
          size === "lg" && variant === "brand" && isDesktop && "h-12 px-6",
          size === "xl" && isDesktop && "body-lg"
        ),
        ref,
        type: asChild ? void 0 : "button",
        ...props
      }
    );
  }
);
Button.displayName = "Button";
const RoundButton = ({
  color = "secondary",
  label,
  icon,
  border = true,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "button",
    {
      className: cn(
        "flex items-center justify-center flex-[0_0_32px] w-8 h-8 rounded-full border border-basic-04",
        color === "primary" && "bg-icon-primary-04 border-primary-02",
        !border && "border-none",
        className
      ),
      "aria-label": label,
      ...props,
      children: /* @__PURE__ */ jsx(
        LineIcon,
        {
          icoName: icon,
          icoColor: color === "primary" ? "var(--color-icon-basic-06)" : "var(--color-icon-basic-01)",
          type: "xs"
        }
      )
    }
  );
};

export { Button, RoundButton, buttonVariants };
